import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import type {
  ChatbotAccessType,
  ChatbotVerificationMethod,
} from '../../types/chatbot.types';

type AccessInputMode = 'email' | 'phone' | 'anonymous';

interface AccessFormProps {
  accessType: ChatbotAccessType;
  allowedDomains?: string[];
  mode: AccessInputMode;
  verificationMethod?: ChatbotVerificationMethod;
  onSubmit: (payload: { email?: string; phone?: string }) => Promise<void>;
  onBack?: () => void;
  isLoading: boolean;
  error?: string | null;
  instanceName?: string;
}

export function AccessForm({
  accessType,
  allowedDomains = [],
  mode,
  verificationMethod = 'NONE',
  onSubmit,
  onBack,
  isLoading,
  error,
  instanceName,
}: AccessFormProps) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const isAnonymousAccess = mode === 'anonymous';
  const requiresEmail = mode === 'email';
  const requiresPhone = mode === 'phone';
  // El instance solo dispara OTP cuando verificationMethod !== NONE; con
  // whitelist/dominio + NONE el submit crea sesión directa, así que el botón
  // debe reflejar esa acción y no "Enviar código de verificación".
  const willSendOtp = (requiresEmail || requiresPhone) && verificationMethod !== 'NONE';

  const validateEmail = (email: string): boolean => {
    const normalizedEmail = email.trim();
    const atIndex = normalizedEmail.indexOf('@');
    const hasSingleAt = atIndex > 0 && atIndex === normalizedEmail.lastIndexOf('@');
    const domain = hasSingleAt ? normalizedEmail.slice(atIndex + 1) : '';
    const hasValidDomain = domain.includes('.') && !domain.startsWith('.') && !domain.endsWith('.');

    if (!hasSingleAt || !hasValidDomain || /\s/.test(normalizedEmail)) {
      setValidationError('Ingresa un email válido');
      return false;
    }

    if (accessType === 'DOMAIN' && allowedDomains.length > 0) {
      const domain = email.split('@')[1];
      if (!allowedDomains.includes(domain)) {
        setValidationError(`El dominio debe ser: ${allowedDomains.join(', ')}`);
        return false;
      }
    }

    setValidationError(null);
    return true;
  };

  const validatePhone = (phoneValue: string): boolean => {
    const phoneRegex = /^\+?[1-9]\d{6,14}$/;
    if (!phoneRegex.test(phoneValue.replace(/\s+/g, ''))) {
      setValidationError('Ingresa un teléfono válido (incluye código de país)');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isAnonymousAccess) {
      await onSubmit({});
      return;
    }

    if (requiresEmail) {
      if (validateEmail(email)) {
        await onSubmit({ email });
      }
      return;
    }

    if (requiresPhone) {
      if (validatePhone(phone)) {
        await onSubmit({ phone });
      }
    }
  };

  const getDescription = () => {
    if (isAnonymousAccess) {
      return 'Comienza a chatear sin necesidad de registro';
    }
    if (requiresPhone) {
      return willSendOtp
        ? 'Ingresa tu número de teléfono para verificar tu acceso'
        : 'Ingresa tu número de teléfono para comenzar';
    }
    if (accessType === 'WHITELIST') {
      return 'Ingresa tu email autorizado para acceder';
    }
    if (accessType === 'DOMAIN') {
      return `Ingresa tu email corporativo (${allowedDomains.join(', ')})`;
    }
    return 'Ingresa tu email para comenzar';
  };

  const getAccessTypeLabel = () => {
    if (accessType === 'ANONYMOUS') return 'Acceso Anónimo';
    if (accessType === 'WHITELIST') return 'Acceso Restringido';
    if (accessType === 'DOMAIN') return 'Acceso Corporativo';
    if (accessType === 'OPEN') return 'Acceso Abierto';
    return 'Chatbot';
  };

  return (
    <div
      data-testid='access-form'
      className='relative flex min-h-screen items-center justify-center overflow-hidden p-4'
    >
      {/* Animated background gradient */}
      <div className='pointer-events-none absolute inset-0 bg-gradient-to-br from-background via-card to-primary-50 dark:from-background dark:via-card dark:to-background' />
      <div className='pointer-events-none absolute inset-0 overflow-hidden'>
        <div className='absolute -top-60 -right-60 h-96 w-96 animate-pulse-slow rounded-full bg-violet-400/10 blur-3xl' />
        <div className='absolute -bottom-60 -left-60 h-96 w-96 animate-pulse-slow rounded-full bg-cyan-400/10 blur-3xl [animation-delay:1.5s]' />
        <div className='absolute top-1/2 left-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 animate-pulse-slow rounded-full bg-gradient-to-r from-violet-300/20 to-cyan-300/20 blur-3xl [animation-delay:3s]' />
      </div>

      {/* Card with CLEAR border */}
      <div className='relative z-10 w-full max-w-md animate-in fade-in-0 zoom-in-95 duration-500'>
        {/* Header */}
        <div className='text-center'>
          <div className='relative mx-auto mb-6 inline-flex'>
            <div className='absolute inset-0 animate-pulse-slow rounded-3xl bg-gradient-to-r from-primary to-primary-700 blur-xl opacity-40' />
            <div className='relative flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-primary to-primary-700 shadow-2xl shadow-violet-500/30'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={1.5}
                stroke='currentColor'
                className='h-10 w-10 text-white'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z'
                />
              </svg>
            </div>
          </div>

          <span className='inline-block rounded-full border border-violet-200 bg-violet-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-violet-700 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300'>
            {getAccessTypeLabel()}
          </span>

          <h1 className='mt-6 text-3xl font-bold text-slate-900 dark:text-white'>
            {instanceName || 'Asistente Virtual'}
          </h1>

          <p className='mt-3 text-slate-600 dark:text-slate-400'>{getDescription()}</p>
        </div>

        {/* Form card - CLEAR BORDER */}
        <div className='mt-8 rounded-3xl border border-slate-200 bg-white p-8 shadow-xl dark:border-slate-700 dark:bg-slate-900'>
          <form onSubmit={handleSubmit} className='space-y-6'>
            {/* Error alert */}
            {(error || validationError) && (
              <div className='flex items-start gap-3 rounded-2xl border-2 border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/50 animate-in fade-in-0 slide-in-from-top-2 duration-300'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth={1.5}
                  stroke='currentColor'
                  className='h-5 w-5 shrink-0 text-red-500'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z'
                  />
                </svg>
                <p className='text-sm font-medium text-red-700 dark:text-red-300'>
                  {error || validationError}
                </p>
              </div>
            )}

            {/* Email input - CLEARLY DELIMITED */}
            {requiresEmail && (
              <div className='space-y-2.5'>
                <Label
                  htmlFor='email'
                  className='text-sm font-semibold text-slate-700 dark:text-slate-300'
                >
                  Correo electrónico
                </Label>
                <div className='relative'>
                  <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      strokeWidth={1.5}
                      stroke='currentColor'
                      className='h-5 w-5 text-slate-400'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75'
                      />
                    </svg>
                  </div>
                  <Input
                    id='email'
                    type='email'
                    placeholder='tu@email.com'
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      setValidationError(null);
                    }}
                    disabled={isLoading}
                    autoComplete='email'
                    autoFocus
                    className='h-12 rounded-xl border-2 border-slate-300 bg-white pl-11 pr-4 text-slate-900 placeholder:text-slate-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:placeholder:text-slate-500'
                  />
                </div>
              </div>
            )}

            {requiresPhone && (
              <div className='space-y-2.5'>
                <Label
                  htmlFor='phone'
                  className='text-sm font-semibold text-slate-700 dark:text-slate-300'
                >
                  Teléfono
                </Label>
                <div className='relative'>
                  <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      strokeWidth={1.5}
                      stroke='currentColor'
                      className='h-5 w-5 text-slate-400'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106a1.125 1.125 0 00-1.173.417l-.97 1.293a1.125 1.125 0 01-1.21.38 12.035 12.035 0 01-7.143-7.143 1.125 1.125 0 01.38-1.21l1.293-.97a1.125 1.125 0 00.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z'
                      />
                    </svg>
                  </div>
                  <Input
                    id='phone'
                    type='tel'
                    placeholder='+54 11 5555 1234'
                    value={phone}
                    onChange={e => {
                      setPhone(e.target.value);
                      setValidationError(null);
                    }}
                    disabled={isLoading}
                    autoComplete='tel'
                    autoFocus
                    className='h-12 rounded-xl border-2 border-slate-300 bg-white pl-11 pr-4 text-slate-900 placeholder:text-slate-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:placeholder:text-slate-500'
                  />
                </div>
              </div>
            )}

            {/* Submit button - CLEAR CALL TO ACTION */}
            <Button
              type='submit'
              disabled={isLoading}
              className={`group relative w-full h-12 rounded-xl text-base font-semibold transition-all duration-200 ${
                isLoading
                  ? 'bg-slate-300 text-slate-500'
                  : 'bg-gradient-to-r from-primary to-primary-700 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
              }`}
            >
              {isLoading ? (
                <>
                  <Spinner className='mr-2 h-5 w-5' />
                  Procesando...
                </>
              ) : (
                <>
                  {willSendOtp ? 'Enviar código de verificación' : 'Iniciar Chat'}
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={2}
                    stroke='currentColor'
                    className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3'
                    />
                  </svg>
                </>
              )}
            </Button>

            {/* Security notice */}
            <div className='flex items-center justify-center gap-2 text-xs text-slate-500'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={1.5}
                stroke='currentColor'
                className='h-4 w-4 text-emerald-500'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              <span>Tu información está protegida</span>
            </div>

            {onBack && !isAnonymousAccess && (
              <button
                type='button'
                onClick={onBack}
                disabled={isLoading}
                className='w-full text-sm text-slate-500 transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              >
                Volver
              </button>
            )}
          </form>
        </div>
      </div>

      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
